import React, { useEffect, useId, useRef, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { useBioLeadForm } from "@/hooks/useBioLeadForm";
import "./BioLeadForm.scss";

const INTENTS = [
  { value: "buy", label: "Buy a home", toForm: true },
  { value: "sell", label: "Sell a home" },
  { value: "both", label: "Buy and sell", toForm: true },
];

export const buyerListingFormUrl = (slug) =>
  `${process.env.REACT_APP_BUYERLISTING_SERVICE_BASE_URL}/form/${encodeURIComponent(slug || "")}`;

const TIMING = [
  { value: "asap", label: "As soon as possible" },
  { value: "1_3", label: "In 1 to 3 months" },
  { value: "3_6", label: "In 3 to 6 months" },
  { value: "6_12", label: "In 6 to 12 months" },
  { value: "12_plus", label: "In a year or more" },
  { value: "exploring", label: "Just exploring" },
];

const LISTED = [
  { value: "no", label: "No" },
  { value: "yes", label: "Yes, with another agent" },
  { value: "expired", label: "It was, but it expired or was withdrawn" },
];

const ALSO_BUYING = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
  { value: "unsure", label: "Not sure yet" },
];

const REASONS = [
  { value: "relocating", label: "Moving for work or family" },
  { value: "upsizing", label: "Need more space" },
  { value: "downsizing", label: "Downsizing" },
  { value: "investment", label: "Selling an investment property" },
  { value: "life_change", label: "A life change" },
  { value: "value", label: "Curious what it is worth" },
];

const CONDITION = [
  { value: "ready", label: "Move-in ready" },
  { value: "updates", label: "Needs some updates" },
  { value: "major", label: "Needs major work" },
];

const CONTACT_BY = [
  { value: "call", label: "Call" },
  { value: "text", label: "Text" },
  { value: "email", label: "Email" },
];

// A preference only makes sense for a channel the visitor actually gave us.
const contactOptions = (form) =>
  CONTACT_BY.filter((option) =>
    option.value === "email" ? form.email.trim() : form.phone.trim(),
  );

const REPLY_COPY = {
  call: "a call",
  text: "a text",
  email: "an email",
};

const LAST_STEP = 3;

const EMPTY = {
  intent: "",
  area: "",
  timing: "",
  listed: "",
  alsoBuying: "",
  reason: "",
  condition: "",
  contactBy: "",
  name: "",
  email: "",
  phone: "",
  note: "",
  website: "",
};

const fieldErrors = (error) => error?.response?.data?.result?.errors || null;

const FIELD_STEP = {
  intent: 0,
  area: 1,
  timing: 1,
  listed: 1,
  note: 2,
};

const Chips = ({ label, options, value, onPick }) => (
  <div className="bio-lead__group" role="group" aria-label={label}>
    <span className="bio-lead__group-label">{label}</span>
    <div className="bio-lead__chips">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          className={`bio-lead__chip${value === option.value ? " is-selected" : ""}`}
          aria-pressed={value === option.value}
          onClick={() => onPick(value === option.value ? "" : option.value)}
        >
          {option.label}
        </button>
      ))}
    </div>
  </div>
);

const firstErroredStep = (errors) => {
  const steps = Object.keys(errors || {})
    .map((key) => FIELD_STEP[key])
    .filter((value) => value !== undefined);
  return steps.length ? Math.min(...steps) : null;
};

const BioLeadForm = ({
  show = true,
  onHide,
  slug,
  firstName,
  inline = false,
}) => {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(EMPTY);
  const openedAt = useRef(Date.now());
  const stepRef = useRef(null);
  const stepMounted = useRef(false);
  const uid = useId();
  const submit = useBioLeadForm(slug);

  useEffect(() => {
    if (stepMounted.current) stepRef.current?.focus();
    stepMounted.current = true;
  }, [step]);

  useEffect(() => {
    if (show) {
      setStep(0);
      setForm(EMPTY);
      submit.reset();
      openedAt.current = Date.now();
    }
  }, [show]);

  useEffect(() => {
    const erroredStep = firstErroredStep(fieldErrors(submit.error));
    if (erroredStep !== null) setStep(erroredStep);
  }, [submit.error]);

  const set = (key) => (event) => {
    if (submit.isError) submit.reset();
    setForm({ ...form, [key]: event.target.value });
  };
  const pick = (key) => (value) => setForm({ ...form, [key]: value });
  const errors = fieldErrors(submit.error) || {};
  const sent = submit.isSuccess;

  const canContinue =
    step === 0
      ? Boolean(form.intent)
      : step === 1
        ? Boolean(form.area.trim() && form.timing && form.listed)
        : step === 2
          ? true
          : Boolean(
              form.name.trim() && (form.email.trim() || form.phone.trim()),
            );

  const send = () =>
    submit.mutate({
      ...form,
      contactBy: contactOptions(form).some((o) => o.value === form.contactBy)
        ? form.contactBy
        : "",
      elapsedMs: Date.now() - openedAt.current,
    });

  const next = () => (step < LAST_STEP ? setStep(step + 1) : send());

  const restart = () => {
    setStep(0);
    setForm(EMPTY);
    submit.reset();
    openedAt.current = Date.now();
  };

  const errorId = (key) => `${uid}-${key}-error`;
  const errorFor = (key) =>
    errors[key] ? (
      <p id={errorId(key)} className="bio-lead__error" role="alert">
        {errors[key]}
      </p>
    ) : null;
  const invalid = (key) =>
    errors[key]
      ? { "aria-invalid": true, "aria-describedby": errorId(key) }
      : {};

  const generalError =
    submit.isError && !fieldErrors(submit.error)
      ? submit.error?.response?.status === 429
        ? "Too many requests right now. Please try again in a little while."
        : "Something went wrong sending this. Please try again."
      : null;

  const content = (
    <>
      {sent ? (
        <div className="bio-lead__done">
          <h2 className="bio-lead__title">Sent to {firstName}</h2>
          <p className="bio-lead__text">
            Expect {REPLY_COPY[form.contactBy] || "a call or email"} from{" "}
            {firstName}.
          </p>
          {form.alsoBuying === "yes" ? (
            <a
              className="bio-lead__next-step"
              href={buyerListingFormUrl(slug)}
              target="_blank"
              rel="noopener noreferrer"
            >
              Buying too? Fill out {firstName}'s Buyer Listing Form&nbsp;&rarr;
            </a>
          ) : null}
          <button
            type="button"
            className="bio-lead__primary"
            onClick={inline ? restart : onHide}
          >
            {inline ? "Send another" : "Done"}
          </button>
        </div>
      ) : (
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (canContinue && !submit.isPending) next();
          }}
        >
          <div className="bio-lead__step" ref={stepRef} tabIndex={-1}>
            <h2 className="bio-lead__title">
              {step === 0
                ? `Tell ${firstName} what you're looking for`
                : `Tell ${firstName} about your home`}
            </h2>
            <div
              className="bio-lead__steps"
              role="img"
              aria-label={`Step ${step + 1} of ${LAST_STEP + 1}`}
            >
              {[0, 1, 2, 3].map((index) => (
                <span key={index} className={index <= step ? "is-on" : ""} />
              ))}
            </div>

            {step === 0 ? (
              <div className="bio-lead__options">
                {INTENTS.map((option) =>
                  option.toForm ? (
                    <a
                      key={option.value}
                      className="bio-lead__option"
                      aria-label={`${option.label} (opens in a new tab)`}
                      href={buyerListingFormUrl(slug)}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {option.label}
                    </a>
                  ) : (
                    <button
                      key={option.value}
                      type="button"
                      className={`bio-lead__option${form.intent === option.value ? " is-selected" : ""}`}
                      onClick={() => {
                        setForm({ ...form, intent: option.value });
                        setStep(1);
                      }}
                    >
                      {option.label}
                    </button>
                  ),
                )}
                {errorFor("intent")}
              </div>
            ) : null}

            {step === 1 ? (
              <div className="bio-lead__fields">
                <label>
                  Your home's address
                  <input
                    value={form.area}
                    onChange={set("area")}
                    {...invalid("area")}
                    placeholder="123 Main St, City"
                    autoComplete="street-address"
                  />
                </label>
                {errorFor("area")}
                <label>
                  When do you want to sell?
                  <select
                    value={form.timing}
                    onChange={set("timing")}
                    {...invalid("timing")}
                  >
                    <option value="">Choose one</option>
                    {TIMING.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                {errorFor("timing")}
                <Chips
                  label="Is it listed with an agent right now?"
                  options={LISTED}
                  value={form.listed}
                  onPick={pick("listed")}
                />
                {errorFor("listed")}
              </div>
            ) : null}

            {step === 2 ? (
              <div className="bio-lead__fields">
                <Chips
                  label="Are you also buying?"
                  options={ALSO_BUYING}
                  value={form.alsoBuying}
                  onPick={pick("alsoBuying")}
                />
                <label>
                  Why are you thinking of selling?
                  <select value={form.reason} onChange={set("reason")}>
                    <option value="">Choose one (optional)</option>
                    {REASONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
                <Chips
                  label="What shape is it in?"
                  options={CONDITION}
                  value={form.condition}
                  onPick={pick("condition")}
                />
                <label>
                  Tell {firstName} about your home and your plans
                  <textarea
                    value={form.note}
                    onChange={set("note")}
                    {...invalid("note")}
                    rows={3}
                    placeholder="Updates you have made, what you love about it, when you would like to move"
                  />
                </label>
                {errorFor("note")}
              </div>
            ) : null}

            {step === 3 ? (
              <div className="bio-lead__fields">
                <label>
                  Your name
                  <input
                    value={form.name}
                    onChange={set("name")}
                    {...invalid("name")}
                    autoComplete="name"
                  />
                </label>
                {errorFor("name")}
                <label>
                  Email
                  <input
                    type="email"
                    value={form.email}
                    onChange={set("email")}
                    {...invalid("email")}
                    autoComplete="email"
                  />
                </label>
                {errorFor("email")}
                <label>
                  Phone
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={set("phone")}
                    {...invalid("phone")}
                    autoComplete="tel"
                  />
                </label>
                {errorFor("phone")}
                <Chips
                  label="Best way to reach you"
                  options={contactOptions(form)}
                  value={form.contactBy}
                  onPick={pick("contactBy")}
                />
                <input
                  className="bio-lead__trap"
                  tabIndex={-1}
                  autoComplete="new-password"
                  aria-hidden="true"
                  value={form.website}
                  onChange={set("website")}
                  name="bio_contact_ref"
                />
              </div>
            ) : null}
          </div>

          {generalError ? (
            <p className="bio-lead__error">{generalError}</p>
          ) : null}

          {step > 0 ? (
            <div className="bio-lead__actions">
              <button
                type="button"
                className="bio-lead__secondary"
                onClick={() => setStep(step - 1)}
              >
                Back
              </button>
              <button
                type="submit"
                className="bio-lead__primary"
                disabled={!canContinue || submit.isPending}
              >
                {step < LAST_STEP
                  ? "Continue"
                  : submit.isPending
                    ? "Sending"
                    : `Send to ${firstName}`}
              </button>
            </div>
          ) : null}
        </form>
      )}
    </>
  );

  if (inline) {
    return <div className="bio-lead bio-lead--inline">{content}</div>;
  }

  return (
    <Modal show={show} onHide={onHide} centered className="bio-lead">
      <Modal.Body>
        <button
          type="button"
          className="bio-lead__close"
          aria-label="Close"
          onClick={onHide}
        >
          ×
        </button>
        {content}
      </Modal.Body>
    </Modal>
  );
};

export default BioLeadForm;
